<template>
  <div ref="confettiAnchor" class="min-h-screen w-screen flex flex-col px-5 pb-5 lg:px-11 lg:pb-11 text-white relative" style="background-color: #020308">
    <img src="../../assets/images/foreplay-logo.png" class="fixed top-4 md:top-8 left-4 md:left-9 h-10 w-auto cursor-pointer" 
    style="z-index: 110" @click="() => { logoutUser() }" >
    <div class="progress-header sticky top-0 w-full h-32 md:h-24 flex items-end justify-center">
      <div class="w-full flex items-center justify-center gap-4 pb-8" style="max-width: 460px">
        <!-- Onboarding Step Progress -->
        <div v-for="(step, index) in Object.keys(onboardingProgressIndication)" :key="`${step}-${index}`" 
        class="flex items-center" :class="{'flex-grow lg:flex-grow-0': !isLastStep(index)}">
          <div class="flex items-center gap-3">
            <!-- Completed step checkmark -->
            <div v-if="indicatorStep > index" class="w-5 h-5 rounded-full" style="background-color: #40C4AA33; color: #40C4AA">
              <CheckmarkIcon />
            </div>
            <div v-else class="w-5 h-5 rounded-full flex items-center justify-center" 
            :style="{border: indicatorStep === index ? 'none' : '1.5px solid #FFFFFF66', backgroundColor: indicatorStep === index ? 'rgba(0, 107, 255, 0.40)' : 'transparent' }">
              <!-- Current step indicator -->
              <div v-if="indicatorStep === index" 
              class="rounded-full w-2 h-2" style="background-color: #1F69FF;" />
            </div>
            <!-- Step label -->
            <BaseText type="label" size="sm" class="capitalize cursor-default"
            :class="indicatorStep === index ? 'text-white' : 'text-neutral-alpha-650 hidden lg:block'">
              {{ step.toLowerCase() }}
            </BaseText>
          </div>
          <!-- Horizontal line between steps -->
          <div v-if="!isLastStep(index)" class="flex-grow lg:flex-grow-0 lg:w-8 h-px bg-neutral-alpha-200 ml-4" />
        </div>
      </div>
    </div>
    <div class="fixed top-4 md:top-8 right-4 md:right-9" style="z-index: 110">
      <button outlined @click="goToTeamInvite" class="flex items-center hover:bg-neutral-alpha-25 rounded-lg p-1.5">
        <TeamSettingsIcon class="text-neutral-alpha-650" />
        <BaseText type="label" size="sm" class="text-white px-1">
          Have an existing team?
        </BaseText>
      </button>
    </div>
    <div v-if="loading" class="w-full flex justify-center items-center mt-16">
      <BaseLoadingLogo />
    </div>
    <div v-else class="w-full flex justify-center items-center text-white mt-2 md:mt-16">
      <TeamOnboardSection
        v-if="onboardingStep === onboardingSteps.TEAM" class="fade-in"
        @next="handleNext"
      />
      <PersonalizeOnboardSection
        v-if="onboardingStep === onboardingSteps.PERSONALIZE" class="fade-in"
        @next="handleNext"
      />
      <PlanOnboardSection
        v-if="onboardingStep === onboardingSteps.PLAN" class="fade-in"
        @next="handleNext"
      />
      <PaymentOnboardSection
        v-if="onboardingStep === onboardingSteps.PAYMENT" class="fade-in"
        :init-plan="onboardingForm.plan"
        @next="handleNext"
        @back="onboardingStep = onboardingSteps.PLAN"
      />
      <FinishOnboardSection
        v-if="onboardingStep === onboardingSteps.FINISH" class="fade-in"
      />
    </div>
    <LensGuestInvitationModal
      v-if="showLensInviteModal"
      @handleAction="handleLensInviteAction"
      @close="showLensInviteModal = false"
    />
    <!-- Hyros email -->
    <input v-if="userHyrosEmail" type="email" id="hyros-user-email" class="hidden absolute" :value="userHyrosEmail">
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import FirebaseAPI from '@/api/firebase'
import GrafanaAPI from '@/api/grafana'
import firebase from '@/api/config/FirebaseConfig'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { SUBSCRIPTION_PLANS, SUBSCRIPTION_STATES } from '@/utils/stripe/productMeta'

// Onboard Components
import PersonalizeOnboardSection from '@/components/account/onboard/PersonalizeOnboardSection.vue'
import PlanOnboardSection from '@/components/account/onboard/PlanOnboardSection.vue'
import PaymentOnboardSection from '@/components/account/onboard/PaymentOnboardSection.vue'
import TeamOnboardSection from '@/components/account/onboard/TeamOnboardSection.vue'
import FinishOnboardSection from '@/components/account/onboard/FinishOnboardSection.vue'
import LensGuestInvitationModal from '@/components/lens/LensGuestInvitationModal.vue'

// Icons
import CheckmarkIcon from '@/components/globals/Icons/CheckmarkIcon.vue'
import TeamSettingsIcon from '@/components/globals/Icons/SettingsIcons/TeamSettingsIcon.vue'
export default {
  name: 'UserOnboardView',
  components: {
    PersonalizeOnboardSection,
    PlanOnboardSection,
    PaymentOnboardSection,
    TeamOnboardSection,
    FinishOnboardSection,
    CheckmarkIcon,
    LensGuestInvitationModal,
    TeamSettingsIcon
  },
  data () {
    return {
      // Step indices where we update the progress indication checkmarks
      onboardingProgressIndication: {
        REGISTER: 0,
        PERSONALIZE: 1,
        CONFIRM: 3,
        SETUP: 5
      },
      // Represents the actual number of steps (for component rendering)
      onboardingSteps: {
        REGISTER: 0,
        TEAM: 1,
        PERSONALIZE: 2,
        PLAN: 3,
        PAYMENT: 4,
        FINISH: 5
      },
      onboardingStep: 1, // We skip the register step - handled in a separate route

      onboardingForm: {
        personalize: {},
        plan: {},
        payment: {}
      },

      loading: true,
      showLensInviteModal: false,
      leadEventSent: false,
    }
  },
  async mounted () {
    const currentUser = firebase.auth().currentUser

    // Check if the user has an active subscription - if they do then they shouldn't be here
    const isTrialing = this.getUserSubscription?.status === 'trialing'
    const hasSubscription = this.getUserSubscription.internalProductId && this.getUserSubscription.status !== SUBSCRIPTION_STATES.NOT_ACTIVE;
    if (isTrialing || hasSubscription) {
      await FirebaseAPI.Users.update(currentUser.uid, { onboardingStatus: 'FINISH' })
      this.SET_USER({ ...this.getUser, onboardingStatus: 'FINISH' })
      console.log('User has an active subscription, pushing to discovery view')
      
      await new Promise(resolve => setTimeout(resolve, 500))
      this.$router.push({ name: 'DiscoveryView' })
      return
    }
    
    /**
     * If the user has a pending lens invitation, show the invite modal
     * If the user has any pending team invitations, skip onboard and app will prompt to join team
     * Otherwise, set the user's onboarding progress based on where they left off (or start from the beginning)
     */
    await this.fetchUserInvitations()
    if (this.getPendingLensInvitations?.length > 0) {
      this.showLensInviteModal = true
    } else if (this.getPendingInvitations?.length > 0 || !this.getUser.onboardingStatus) {
      // June 23 - disabled onboard skip and let user manually trigger via team invite flow
      // If the user has any pending team invitations (or they're an old user from before onboarding was implemented), we allow them to skip the onboarding
      // this.onboardingStep = this.onboardingSteps.FINISH
      // this.updateUserOnboardingStatus(this.onboardingSteps.FINISH)
    } else {
      // Set the user's onboarding progress based on where they left off (or start from the beginning)
      this.onboardingStep = this.onboardingSteps[this.getUser.onboardingStatus]
      if (this.getUser.onboardingForm) {
        this.onboardingForm = {
          ...this.getUser.onboardingForm,
          plan: SUBSCRIPTION_PLANS.find(plan => plan.key === this.getUser.onboardingForm?.plan?.key) ?? SUBSCRIPTION_PLANS[1]
        }
      }
    }

    // If the user is starting on the TEAM step, we send it to grafana
    if (this.onboardingStep === this.onboardingSteps.TEAM) {
      try {
        await GrafanaAPI.Onboarding.sendEvent('TEAM')
      } catch (error) {
        console.error(error)
      }
    }

    this.loading = false
  },
  watch: {
    onboardingStep (currentStep) {
      // Play the confetti animation when the onboarding is finished
      if (currentStep === this.onboardingSteps.FINISH) {
        this.$confetti.start({ defaultType: 'rect' })
        setTimeout(() => { this.$confetti.stop() }, 750)
      }
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getUserSubscription', 'getTeamSubscriptionState', 'getPendingInvitations', 'getPendingLensInvitations']),
    userHyrosEmail () {
      return this.$route?.query?.email || this.getUser?.email
    },
    indicatorStep () {
      // Get the index of the current step on the progress indicator
      const indicatorSteps = Object.entries(this.onboardingProgressIndication)
      let indicatorIndex = 0
      for (let i = 0; i < indicatorSteps.length; i++) {
        const [_, threshold] = indicatorSteps[i]
        if (this.onboardingStep >= threshold) indicatorIndex = i
        else break
      }
      return indicatorIndex
    }
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER', 'SET_PENDING_LENS_GUEST_INVITATIONS']),
    ...mapActions('AuthModule', ['fetchUserInvitations', 'logoutUser']),
    goToTeamInvite () {
      this.$router.push({ name: 'TeamInviteView' })
    },
    handleStepClick (stepIndex) {
      this.onboardingStep = stepIndex
    },
    handleNext (submittedData = null) {
      if (submittedData?.step && submittedData?.data) {
        const { step, data } = submittedData
        this.onboardingForm[step] = data

        // Submit personalization data to update in HubSpot (if applicable)
        if (step === 'personalize') {
          const payload = {
            industry: data.work,
            referrer: data.referrer
          }
          console.log('Submitting personalization data')
          try {
            ForeplayAPI.Users.submitOnboardingPersonalization(payload)
            GrafanaAPI.Onboarding.sendPersonalizationInfo(payload)
          } catch (error) {
            console.error(error)
          }
        }
      }

      this.onboardingStep++

      // Update the user's onboarding status
      this.updateUserOnboardingStatus(this.onboardingStep)

      // Reset scroll position to top
      window.scrollTo(0, 0)
    },
    isLastStep (index) {
      return index === Object.keys(this.onboardingProgressIndication).length - 1
    },
    async updateUserOnboardingStatus (stepIndex) {
      const stepKey = Object.keys(this.onboardingSteps)[stepIndex]
      const currentUser = firebase.auth().currentUser

      // Update the user's onboarding status in firestore and Vuex, and send the onboarding event to grafana
      try {
        await FirebaseAPI.Users.update(currentUser.uid, { onboardingStatus: stepKey })

        const updatedUser = { ...this.getUser }
        updatedUser.onboardingStatus = stepKey
        updatedUser.onboardingForm = this.onboardingForm
        this.SET_USER(updatedUser)

        FirebaseAPI.Users.update(currentUser.uid, { onboardingForm: {
          personalize: this.onboardingForm?.personalize ?? {},
          plan: { key: this.onboardingForm?.plan?.key ?? 'workflow_plan', interval: this.onboardingForm?.plan?.interval ?? 'month' },
          payment: { cardBillingAddress: this.onboardingForm?.payment?.cardBillingAddress ?? {} }
        } })
        GrafanaAPI.Onboarding.sendEvent(stepKey)
      } catch (error) {
        console.error(error)
      }

      // ======= MARKETING EVENTS =======

      // Submit lead when user gets to the plan selection step
      if (stepKey === 'PLAN' && !this.leadEventSent) {
        this.leadEventSent = true
        try {
          ForeplayAPI.Marketing.submitAbleCDPEvent({
            event_type: "Lead",
            resource: {
              description: "User reached plan selection step",
            }
          })
        } catch (error) {
          console.error('Failed to submit lead to Able CDP', error)
        }
      }

      // Submit relevant marketing events when the user completes the onboarding flow
      if (stepKey === 'FINISH') {
        const email = currentUser.email
        window.dataLayer.push({
          event: 'app_sign_up',
          user_data: {
            ...(this.getUser?.name && { name: this.getUser.name }),
            email_address: email
          }
        })

        if (!_.isEmpty(this.onboardingForm?.plan) && !_.isEmpty(this.onboardingForm?.payment)) {
          // Send GTM event for payment info
          window.dataLayer.push({
            event: 'addpaymentinfo',
            user_data: {
              ...(this.getUser?.name && { name: this.getUser.name }),
              email_address: email
            },
            plan_info: {
              name: this.onboardingForm?.plan?.name,
              interval: this.onboardingForm?.plan?.interval,
              price: this.onboardingForm?.plan?.price,
              included_users: this.onboardingForm?.plan?.includedUsers,
              lens_count: this.onboardingForm?.plan?.lensCount,
              spyder_count: this.onboardingForm?.plan?.spyderCount
            },
            payment_info: {
              ...(this.onboardingForm?.payment?.cardBillingAddress)
            }
          })
          // Submit plan info to grafana
          try {
            const payload = {
              plan: this.onboardingForm?.plan?.key,
              interval: this.onboardingForm?.plan?.interval
            }
            GrafanaAPI.Onboarding.sendPlanInfo(payload)
          } catch (error) {
            console.error(error)
          }

          // Submit the add payment and trial started events to Able CDP
          try {
            ForeplayAPI.Marketing.submitAbleCDPEvent({
              event_type: "AddPaymentInfo",
              resource: {
                description: "Payment info added",
                custom_data: {
                  billing_address: {
                    ...(this.onboardingForm?.payment?.cardBillingAddress)
                  }
                }
              }
            })
            ForeplayAPI.Marketing.submitAbleCDPEvent({
              event_type: "StartTrial",
              resource: { 
                deal_value: this.onboardingForm?.plan?.price,
                deal_currency: 'USD',
                deal_items: [
                  {
                    name: this.onboardingForm?.plan?.name,
                    qty: this.onboardingForm?.plan?.includedUsers,
                    category: this.onboardingForm?.plan?.interval
                  }
                ],
                custom_data: {
                  included_users: this.onboardingForm?.plan?.includedUsers,
                  lens_count: this.onboardingForm?.plan?.lensCount,
                  spyder_count: this.onboardingForm?.plan?.spyderCount
                }
              }
            })
          } catch (error) {
            console.error('Failed to submit StartTrial to Able CDP', error)
          }
        }
      }
    },
    handleLensInviteAction (isAccept) {
      if (isAccept) {
        this.SET_PENDING_LENS_GUEST_INVITATIONS([])
        this.onboardingStep = this.onboardingSteps.FINISH
        this.updateUserOnboardingStatus(this.onboardingSteps.FINISH)
        // Clean up store invitations
      }
    }
  }
}
</script>

<style scoped>
:global(body) {
  overflow-x: hidden;
}
.progress-header {
  z-index: 100;
  /* background: linear-gradient(to bottom, #020308 0%, #020308 72px, transparent); */
  background: linear-gradient(to top, transparent 0%, #020308 24px, #020308);
}
.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>